//------------------------------------------------------------------------------
// BFA10104G 共架申込 通信設備 - 地図
//------------------------------------------------------------------------------
import React from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import { doShowMessage } from '@/reducks/common/operations';
import { StandardButton } from '@/components/atoms/Buttons/PositiveButton';
import backend from '@/common/backend';
import PoleItem from '@/utils/pole-item';
import PoleLink from '@/utils/pole-link';
import KeikanMapBody from './map-body';
import KeikanMapNone from './map-none';
import ItemSelected from './item-selected';
import { k1pole, k2pole } from './kkn-utils';
import { PoleSelectForm, PoleSelectList } from './pole-list';

// 定数
const LOGH = 'BFA10104G map area:';

/**
 * 地図表示エリア
 */
export default function OrderMapArea({
  mainPole,
  setubiList = [],
  keikanList = [],
  setubiSelected = null,
  keikanSelected = null,
  onSetubiAdd, onSetubiEdit, onSetubiDelete,
  onKeikanAdd, onKeikanEdit, onKeikanDelete,
}) {

  // 状態
  const [ warnings, setWarnings ] = React.useState([]);   // メッセージ
  const [ poleList, setPoleList ] = React.useState([]);   // 電柱一覧

  // 選択電柱/径間
  const [ itemSelected, setItemSelected ] = React.useState(null);

  // 申込電柱一覧 (重複のない電柱マップ)
  const orderPoles = React.useMemo(() => {
    const poles = new Map();

    // 径間一覧
    keikanList?.forEach((kkn) => {
      const pole1 = k1pole(kkn);
      const pole2 = k2pole(kkn);
      poles.set(pole1.key(), pole1);
      poles.set(pole2.key(), pole2);
    });

    // 電柱一覧
    setubiList?.forEach((stb) => {
      const pole = new PoleItem(stb);
      poles.set(pole.key(), pole);
    });

    // 電柱マップ
    return poles;
  }, [setubiList, keikanList]);

  // メッセージ表示
  const dispatch = useDispatch();
  const showMessage = (props) => dispatch(doShowMessage(props));

  // 上位で設備選択 - 選択電柱更新
  React.useEffect(() => {

    // 選択解除なら何もしない
    if (!setubiSelected) return;

    // 選択した設備の電柱を状態保存
    const poleSelected = itemSelected?.pole;
    if (!poleSelected || !poleSelected.same(setubiSelected)) {
      const pole = new PoleItem(setubiSelected);
      setItemSelected(ItemSelected.pole(pole));
    }
  }, [setubiSelected]);

  // 上位で径間選択 - 選択径間更新
  React.useEffect(() => {

    // 選択解除なら何もしない
    if (!keikanSelected) return;

    // 選択した径間の電柱リンクを状態保存
    const link = new PoleLink(
        new PoleItem({
          areaCode:  keikanSelected.k1areaCode,
          areaName:  keikanSelected.k1areaName,
          lineCode:  keikanSelected.k1lineCode,
          lineName:  keikanSelected.k1lineName,
          poleNo:    keikanSelected.k1poleNo,
          poleKind:  keikanSelected.k1poleKind,
          latitude:  keikanSelected.k1latitude,
          longitude: keikanSelected.k1longitude,
        }),
        new PoleItem({
          areaCode:  keikanSelected.k2areaCode,
          areaName:  keikanSelected.k2areaName,
          lineCode:  keikanSelected.k2lineCode,
          lineName:  keikanSelected.k2lineName,
          poleNo:    keikanSelected.k2poleNo,
          poleKind:  keikanSelected.k2poleKind,
          latitude:  keikanSelected.k2latitude,
          longitude: keikanSelected.k2longitude,
        }),
    );
    setItemSelected(ItemSelected.link(link));
  }, [keikanSelected]);

  //----------------------------------------------------------------------------
  // イベント
  //----------------------------------------------------------------------------
  // 電柱検索ボタン
  const handlePoleSearch = React.useCallback(async (formValues) => {
    console.info(LOGH, '電柱検索', formValues);

    // 電柱検索
    const res = await backend.post('/master/poles', formValues);
    const poleList = res
        .parse(showMessage)
        .parseBodyAsArray({
          max: 100,             // 最大100件
          error: 'BFY000015W',  // 上限超えメッセージ
        });

    // ステート更新
    updatePoleList(poleList, res.messages);
  }, []);

  // 申込電柱表示ボタン
  const handleOrderPoles = React.useCallback(() => {
    updatePoleList(Array.from(orderPoles.values()));
  }, [orderPoles]);

  // 電柱一覧更新
  const updatePoleList = React.useCallback((newPoleList, newWarnings) => {
    setItemSelected(null);
    setPoleList(newPoleList ?? []);
    setWarnings(newWarnings ?? []);
  }, []);

  // 電柱/径間選択
  const handleItemSelect = React.useCallback((type, item, opts) => {
    switch (type) {
      case 'pole': setItemSelected(ItemSelected.pole(item, opts)); break;
      case 'link': setItemSelected(ItemSelected.link(item, opts)); break;
      default: setItemSelected(null); break;
    }
  }, []);

  //----------------------------------------------------------------------------
  // 描画
  //----------------------------------------------------------------------------
  return (
    <Stack direction="column">

      {/* [上段] 電柱検索パネル */}
      <Box
        sx={{
          px: 1, py: 1,
          borderTop: 1, borderColor: 'grey.400',  // 区切り線
          display: 'flex',
          alignItems: 'center',
        }}
      >
        {/* 電柱検索フォーム */}
        <PoleSelectForm onSearch={handlePoleSearch} />

        {/* 設備一覧/径間一覧の電柱を表示 */}
        <Box sx={{ ml: 1 }}>
          <Tooltip arrow placement="top"
            title="申込登録中の電柱を表示します。"
          >
            <StandardButton
              variant="contained" size="medium"
              onClick={handleOrderPoles}
            >
              申込電柱表示
            </StandardButton>
          </Tooltip>
        </Box>
      </Box>

      {/*
        * [下段] コンテンツ本体
        */}
      <Stack
        direction="row" spacing={1}
        sx={{ pt: 1, height: '70vh' }}
      >
          {/* (左側) 電柱一覧 */}
          <Box>
            <PoleSelectList
              warnings={warnings}
              poleList={poleList}
              orderPoles={orderPoles}
              itemSelected={itemSelected}
              onItemSelect={handleItemSelect}
              onSetubiAdd={onSetubiAdd}
              onKeikanAdd={onKeikanAdd}
            />
          </Box>

          {/* (右側) 地図表示 */}
          <Box sx={{ flexGrow: 1 }}>
          {
            mainPole?.drawable()

              // 地図表示
              ? <KeikanMapBody
                  mainPole={mainPole}
                  poleList={poleList}
                  setubiList={setubiList}
                  keikanList={keikanList}
                  orderPoles={orderPoles}
                  itemSelected={itemSelected}

                  // イベント
                  onPoleListSelect={updatePoleList}
                  onItemSelect={handleItemSelect}

                  // 設備一覧
                  onSetubiAdd={onSetubiAdd}
                  onSetubiEdit={onSetubiEdit}
                  onSetubiDelete={onSetubiDelete}

                  // 径間一覧
                  onKeikanAdd={onKeikanAdd}
                  onKeikanEdit={onKeikanEdit}
                  onKeikanDelete={onKeikanDelete}
                />

              // 地図なし
              : <KeikanMapNone mainPole={mainPole} />
          }
          </Box>
      </Stack>
    </Stack>
  );
}

// プロパティ
OrderMapArea.propTypes = {
  mainPole:       PropTypes.object,    // 代表電柱
  setubiList:     PropTypes.array,     // 設備リスト
  keikanList:     PropTypes.array,     // 径間リスト
  setubiSelected: PropTypes.object,    // 選択した設備
  keikanSelected: PropTypes.object,    // 選択した径間

  // イベント
  onSetubiAdd:    PropTypes.func.isRequired,   // 設備追加
  onSetubiEdit:   PropTypes.func.isRequired,   // 設備編集
  onSetubiDelete: PropTypes.func.isRequired,   // 設備削除
  onKeikanAdd:    PropTypes.func.isRequired,   // 設備追加
  onKeikanEdit:   PropTypes.func.isRequired,   // 設備編集
  onKeikanDelete: PropTypes.func.isRequired,   // 設備削除
};
